import React, { useState, useMemo } from 'react';
import { useAdmin } from '../../contexts/AdminContext';

const AdminReviews: React.FC = () => {
  const { reviews, approveReview, rejectReview, replyToReview, adminUser } = useAdmin();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterRating, setFilterRating] = useState<number>(0);
  const [sortBy, setSortBy] = useState<'createdAt' | 'rating' | 'productName'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedReview, setSelectedReview] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedReviewDetail, setSelectedReviewDetail] = useState<any>(null);

  // Filtered and sorted Review
  const filteredAndSortedReviews = useMemo(() => {
    let filtered = reviews.filter(review => {
      const matchesSearch = (review.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                           review.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (review.productName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (review.userName || "").toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !filterStatus || review.status === filterStatus;
      const matchesRating = filterRating === 0 || review.rating === filterRating;
      return matchesSearch && matchesStatus && matchesRating;
    });

    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'createdAt':
          aValue = new Date(a.createdAt || a.created_at || new Date());
          bValue = new Date(b.createdAt || b.created_at || new Date());
          break;
        case 'rating':
          aValue = a.rating;
          bValue = b.rating;
          break;
        case 'productName':
          aValue = (a.productName || "").toLowerCase();
          bValue = (b.productName || "").toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [reviews, searchTerm, filterStatus, filterRating, sortBy, sortOrder]);

  const handleApprove = (reviewId: string) => {
    if (window.confirm('Would you like to approve this review?')) {
      approveReview(reviewId, adminUser?.name || 'Admin');
    }
  };

  const handleReject = (reviewId: string) => {
    if (window.confirm('Would you like to reject this review?')) {
      rejectReview(reviewId, adminUser?.name || 'Admin');
    }
  };

  const handleReply = (reviewId: string) => {
    setSelectedReview(reviewId);
    const review = reviews.find(r => r.id === reviewId);
    setReplyText(review?.adminReply || '');
    setReplyModalOpen(true);
  };

  const handleSubmitReply = () => {
    if (!selectedReview || !replyText.trim()) return;

    replyToReview(selectedReview, replyText.trim(), adminUser?.name || 'Admin');
    setReplyModalOpen(false);
    setSelectedReview(null);
    setReplyText('');
  };

  const handleViewDetail = (review: any) => {
    setSelectedReviewDetail(review);
    setDetailModalOpen(true);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case 'pending':
        return { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800' };
      case 'approved':
        return { label: 'Approved', color: 'bg-green-100 text-green-800' };
      case 'rejected':
        return { label: 'Rejected', color: 'bg-red-100 text-red-800' };
      default:
        return { label: status, color: 'bg-neutral-100 text-neutral-800' };
    }
  };

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, i) => (
      <svg
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-neutral-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const stats = {
    total: reviews.length,
    pending: reviews.filter(r => (r.status || (r.is_verified ? "approved" : "pending")) === "pending").length,
    approved: reviews.filter(r => (r.status || (r.is_verified ? "approved" : "pending")) === "approved").length,
    rejected: reviews.filter(r => (r.status || (r.is_verified ? "approved" : "pending")) === "rejected").length,
    avgRating: reviews.length > 0 ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) : '0.0'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Review Management</h1>
          <p className="mt-2 text-sm text-neutral-700">
            {reviews.length} review(s) registered in total.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-forest-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">All Reviews</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.total}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Pending Review</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.pending}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Approved</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.approved}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Rejected</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.rejected}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-neutral-500 truncate">Average Rating</dt>
                  <dd className="text-lg font-medium text-neutral-900">{stats.avgRating}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-neutral-700 mb-1">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md leading-5 bg-white placeholder-neutral-500 focus:outline-none focus:placeholder-neutral-400 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                  placeholder="Search by title, content, or product name..."
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status-filter" className="block text-sm font-medium text-neutral-700 mb-1">
                Status
              </label>
              <select
                id="status-filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Rating Filter */}
            <div>
              <label htmlFor="rating-filter" className="block text-sm font-medium text-neutral-700 mb-1">
                Rating
              </label>
              <select
                id="rating-filter"
                value={filterRating}
                onChange={(e) => setFilterRating(parseInt(e.target.value))}
                className="block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
              >
                <option value={0}>All Ratings</option>
                <option value={5}>5 Stars</option>
                <option value={4}>4 Stars</option>
                <option value={3}>3 Stars</option>
                <option value={2}>2 Stars</option>
                <option value={1}>1 Star</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label htmlFor="sort" className="block text-sm font-medium text-neutral-700 mb-1">
                Sort
              </label>
              <div className="flex space-x-2">
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                >
                  <option value="createdAt">Date Created</option>
                  <option value="rating">Rating</option>
                  <option value="productName">Product Name</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-neutral-300 rounded-md shadow-sm bg-white hover:bg-neutral-50 focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredAndSortedReviews.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-neutral-900">No reviews found</h3>
            <p className="mt-1 text-sm text-neutral-500">Waiting for new reviews.</p>
          </div>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {filteredAndSortedReviews.map((review) => {
              const statusBadge = getStatusBadge(review.status);
              return (
                <li key={review.id} className="hover:bg-neutral-50">
                  <div className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-sm font-medium text-neutral-900 truncate">
                            {review.title}
                          </h3>
                          <div className="flex items-center">
                            {renderStars(review.rating)}
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                            {statusBadge.label}
                          </span>
                        </div>
                        
                        <div className="text-sm text-neutral-500 mb-2">
                          <span className="font-medium">{review.productName}</span>
                          <span className="mx-2">•</span>
                          <span>{review.userName}</span>
                          <span className="mx-2">•</span>
                          <span>{formatDate(review.createdAt || review.created_at)}</span>
                        </div>
                        
                        <div className="text-sm text-neutral-600 mb-3">
                          {review.content.length > 150 
                            ? `${review.content.substring(0, 150)}...` 
                            : review.content
                          }
                        </div>

                        {review.images && review.images.length > 0 && (
                          <div className="flex space-x-2 mb-3">
                            {review.images.slice(0, 3).map((image, index) => (
                              <img
                                key={index}
                                src={image}
                                alt={`Review Image ${index + 1}`}
                                className="w-16 h-16 object-cover rounded border border-neutral-200"
                              />
                            ))}
                            {review.images.length > 3 && (
                              <div className="w-16 h-16 bg-neutral-100 rounded border border-neutral-200 flex items-center justify-center text-xs text-neutral-500">
                                +{review.images.length - 3}
                              </div>
                            )}
                          </div>
                        )}

                        {review.adminReply && (
                          <div className="mt-3 p-3 bg-forest-50 rounded-lg">
                            <div className="text-xs text-forest-400 font-medium mb-1">
                              Admin Reply ({review.reviewedBy} • {formatDate(review.reviewedAt!)})
                            </div>
                            <div className="text-sm text-forest-900">
                              {review.adminReply}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="ml-6 flex flex-col space-y-2">
                        <button
                          onClick={() => handleViewDetail(review)}
                          className="inline-flex items-center px-3 py-1.5 border border-neutral-300 shadow-sm text-xs font-medium rounded text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
                        >
                          View Details
                        </button>
                        
                        {(review.status || (review.is_verified ? "approved" : "pending")) === "pending" && (
                          <>
                            <button
                              onClick={() => handleApprove(review.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(review.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        
                        <button
                          onClick={() => handleReply(review.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-forest-700 bg-forest-100 hover:bg-forest-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
                        >
                          {review.adminReply ? 'Edit Reply' : 'Write Reply'}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Reply Modal */}
      {replyModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-forest-100">
                  <svg className="h-6 w-6 text-forest-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-neutral-900">
                    Review Reply
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-neutral-500">
                      Please write a reply to the customer's review.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={6}
                  className="block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                  placeholder="Enter reply content..."
                />
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleSubmitReply}
                  disabled={!replyText.trim()}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-forest-400 text-base font-medium text-white hover:bg-forest-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Reply
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReplyModalOpen(false);
                    setSelectedReview(null);
                    setReplyText('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-neutral-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModalOpen && selectedReviewDetail && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-neutral-900">
                    Review Details
                  </h3>
                  <button
                    onClick={() => setDetailModalOpen(false)}
                    className="rounded-md text-neutral-400 hover:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-forest-500"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Product Name</label>
                    <p className="mt-1 text-sm text-neutral-900">{selectedReviewDetail.productName}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Author</label>
                      <p className="mt-1 text-sm text-neutral-900">{selectedReviewDetail.userName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Email</label>
                      <p className="mt-1 text-sm text-neutral-900">{selectedReviewDetail.userEmail}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Rating</label>
                      <div className="mt-1 flex items-center">
                        {renderStars(selectedReviewDetail.rating)}
                        <span className="ml-2 text-sm text-neutral-500">({selectedReviewDetail.rating}/5)</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Created</label>
                      <p className="mt-1 text-sm text-neutral-900">{formatDate(selectedReviewDetail.createdAt || selectedReviewDetail.created_at)}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Title</label>
                    <p className="mt-1 text-sm text-neutral-900">{selectedReviewDetail.title}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Content</label>
                    <div className="mt-1 p-3 bg-neutral-50 rounded-md">
                      <p className="text-sm text-neutral-900 whitespace-pre-wrap">{selectedReviewDetail.content}</p>
                    </div>
                  </div>

                  {selectedReviewDetail.images && selectedReviewDetail.images.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Attached Images</label>
                      <div className="mt-1 grid grid-cols-3 gap-2">
                        {selectedReviewDetail.images.map((image: string, index: number) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Review Image ${index + 1}`}
                            className="w-full h-24 object-cover rounded border border-neutral-200"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-neutral-700">Status</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(selectedReviewDetail.status).color}`}>
                        {getStatusBadge(selectedReviewDetail.status).label}
                      </span>
                    </div>
                  </div>

                  {selectedReviewDetail.adminReply && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Admin Reply</label>
                      <div className="mt-1 p-3 bg-forest-50 rounded-md">
                        <div className="text-xs text-forest-400 font-medium mb-2">
                          {selectedReviewDetail.reviewedBy} • {formatDate(selectedReviewDetail.reviewedAt)}
                        </div>
                        <p className="text-sm text-forest-900 whitespace-pre-wrap">{selectedReviewDetail.adminReply}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;